// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Output Validation for Vaccine Model Results
 * Validates QALY and budget impact values from backend calculations
 * Uses configurable thresholds as heuristics (not hard rules)
 */

export type ValidationSeverity = 'ok' | 'warning' | 'error';

export interface ValidationIssue {
  field: 'qaly' | 'budgetImpact' | 'cost' | 'other';
  code: string; // e.g. 'QALY_TOO_HIGH_PER_PERSON', 'BUDGET_TOTAL_VERY_LARGE', 'VALIDATION_NAN_VALUE'
  severity: ValidationSeverity;
  message: string; // user-facing message
  details?: unknown; // raw numbers for logging/debugging
}

export interface ValidationResult {
  severity: ValidationSeverity; // overall severity (worst issue)
  issues: ValidationIssue[];
}

export interface ValidationContext {
  population?: number;
  timeHorizon?: number;
}

/**
 * Configurable thresholds for QALY validation
 * These are heuristics, not hard rules - large programs over many years may legitimately exceed these values
 */
export const DEFAULT_QALY_THRESHOLDS = {
  perPersonYear: 1.0, // heuristic: >1.0 QALY per person per year is very high (should be ≤1.0 for perfect health)
  total: 200000, // heuristic: >200k total is large (context-dependent)
} as const;

/**
 * Configurable thresholds for budget impact validation
 * These are heuristics, not hard rules - large countries, multi-year horizons, or expensive vaccines may legitimately exceed these values
 */
export const DEFAULT_BUDGET_THRESHOLDS = {
  total: 1_000_000_000, // €1B - generic "big number" warning
  perPersonYear: 50_000, // €50k per person per year is very high
} as const;

/**
 * Validate QALY values from backend calculations
 * 
 * @param qalyValue - QALY value to validate (may be NaN)
 * @param context - Optional context (population, timeHorizon) for per-person metrics
 * @param thresholds - Optional custom thresholds (defaults to DEFAULT_QALY_THRESHOLDS)
 * @returns ValidationResult with issues found
 */
export function validateQalys(
  qalyValue: number,
  context: ValidationContext = {},
  thresholds = DEFAULT_QALY_THRESHOLDS
): ValidationResult {
  const issues: ValidationIssue[] = [];

  // Check for NaN values (critical error)
  if (Number.isNaN(qalyValue)) {
    issues.push({
      field: 'qaly',
      code: 'VALIDATION_NAN_VALUE',
      severity: 'error',
      message: 'QALY value is invalid (NaN). This indicates a backend calculation error.',
      details: { qalyValue, context },
    });
    return {
      severity: 'error',
      issues,
    };
  }

  // Compute metrics
  const total = qalyValue;
  const perPersonYear = context.population && context.timeHorizon
    ? total / (context.population * context.timeHorizon)
    : undefined;

  // Check total QALYs
  if (total > thresholds.total) {
    issues.push({
      field: 'qaly',
      code: 'QALY_TOTAL_VERY_LARGE',
      severity: 'warning',
      message: `Total QALYs are very large (${total.toLocaleString()}); this might be expected for large populations, but please verify inputs.`,
      details: {
        total,
        threshold: thresholds.total,
        population: context.population,
        timeHorizon: context.timeHorizon,
      },
    });
  }

  // Check per-person-year QALYs
  if (perPersonYear !== undefined && perPersonYear > thresholds.perPersonYear) {
    issues.push({
      field: 'qaly',
      code: 'QALY_PER_PERSON_YEAR_VERY_LARGE',
      severity: 'warning',
      message: `QALYs per person per year are very high (${perPersonYear.toFixed(3)}); this may indicate a calculation error.`,
      details: {
        perPersonYear,
        threshold: thresholds.perPersonYear,
        total,
        population: context.population,
        timeHorizon: context.timeHorizon,
      },
    });
  }

  // Determine overall severity
  const severity: ValidationSeverity = issues.length === 0
    ? 'ok'
    : issues.some(i => i.severity === 'error')
    ? 'error'
    : 'warning';

  return {
    severity,
    issues,
  };
}

/**
 * Validate budget impact values from backend calculations
 * 
 * @param budgetImpact - Budget impact value to validate (may be NaN)
 * @param context - Optional context (population, timeHorizon) for per-person metrics
 * @param thresholds - Optional custom thresholds (defaults to DEFAULT_BUDGET_THRESHOLDS)
 * @returns ValidationResult with issues found
 */
export function validateBudgetImpact(
  budgetImpact: number,
  context: ValidationContext = {},
  thresholds = DEFAULT_BUDGET_THRESHOLDS
): ValidationResult {
  const issues: ValidationIssue[] = [];

  // Check for NaN values (critical error)
  if (Number.isNaN(budgetImpact)) {
    issues.push({
      field: 'budgetImpact',
      code: 'VALIDATION_NAN_VALUE',
      severity: 'error',
      message: 'Budget impact value is invalid (NaN). This indicates a backend calculation error.',
      details: { budgetImpact, context },
    });
    return {
      severity: 'error',
      issues,
    };
  }

  // Compute metrics
  const total = budgetImpact;
  const costPerPersonYear = context.population && context.timeHorizon
    ? total / (context.population * context.timeHorizon)
    : undefined;

  // Check total budget impact
  if (total > thresholds.total) {
    issues.push({
      field: 'budgetImpact',
      code: 'BUDGET_TOTAL_VERY_LARGE',
      severity: 'warning',
      message: `Budget impact is very large (€${total.toLocaleString()}); verify this is expected for your scenario.`,
      details: {
        total,
        threshold: thresholds.total,
        population: context.population,
        timeHorizon: context.timeHorizon,
      },
    });
  }

  // Check per-person-year budget impact
  if (costPerPersonYear !== undefined && costPerPersonYear > thresholds.perPersonYear) {
    issues.push({
      field: 'budgetImpact',
      code: 'BUDGET_PER_PERSON_YEAR_VERY_LARGE',
      severity: 'warning',
      message: `Budget impact per person per year is very high (€${costPerPersonYear.toLocaleString()}); this may indicate a calculation error.`,
      details: {
        costPerPersonYear,
        threshold: thresholds.perPersonYear,
        total,
        population: context.population,
        timeHorizon: context.timeHorizon,
      },
    });
  }

  // Determine overall severity
  const severity: ValidationSeverity = issues.length === 0
    ? 'ok'
    : issues.some(i => i.severity === 'error')
    ? 'error'
    : 'warning';

  return {
    severity,
    issues,
  };
}

/**
 * Combine multiple validation results into a single overall result
 */
export function combineValidationResults(...results: ValidationResult[]): ValidationResult {
  const allIssues: ValidationIssue[] = [];
  
  for (const result of results) {
    allIssues.push(...result.issues);
  }

  // Determine overall severity (worst issue)
  const severity: ValidationSeverity = allIssues.length === 0
    ? 'ok'
    : allIssues.some(i => i.severity === 'error')
    ? 'error'
    : 'warning';

  return {
    severity,
    issues: allIssues,
  };
}

